/**
 * Toast notification container and individual toast component.
 * Renders above BottomNav at bottom-center (mobile) / bottom-right (desktop).
 */

import { useToast } from '../store/ToastContext';
import type { ToastVariant } from '../store/ToastContext';

// ── Variant config ─────────────────────────────────────────────

const variantStyles: Record<ToastVariant, string> = {
  success: 'border-success/40 bg-success/10 text-success',
  error: 'border-danger/40 bg-danger/10 text-danger',
  warning: 'border-warning/40 bg-warning/10 text-warning',
  info: 'border-accent/40 bg-accent/10 text-accent',
};

const variantIcons: Record<ToastVariant, string> = {
  success: 'M5 13l4 4L19 7',
  error: 'M6 18L18 6M6 6l12 12',
  warning: 'M12 9v4m0 4h.01M12 3l9.66 16.59A1 1 0 0120.84 22H3.16a1 1 0 01-.82-2.41L12 3z',
  info: 'M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z',
};

const ariaLive: Record<ToastVariant, 'assertive' | 'polite'> = {
  success: 'polite',
  info: 'polite',
  warning: 'assertive',
  error: 'assertive',
};

// ── Toast Container ────────────────────────────────────────────

export function ToastContainer() {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-20 left-1/2 -translate-x-1/2 sm:left-auto sm:right-4 sm:translate-x-0 z-50 flex flex-col gap-2 w-[calc(100%-2rem)] max-w-sm pointer-events-none"
      data-testid="toast-container"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="alert"
          aria-live={ariaLive[toast.variant]}
          data-testid={`toast-${toast.id}`}
          className={`pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg backdrop-blur-sm animate-toast-in motion-reduce:animate-none ${variantStyles[toast.variant]}`}
        >
          {/* Icon */}
          <svg
            className="h-5 w-5 shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d={variantIcons[toast.variant]} />
          </svg>

          {/* Message */}
          <p className="flex-1 text-sm font-medium">{toast.message}</p>

          {/* Close */}
          <button
            type="button"
            onClick={() => removeToast(toast.id)}
            className="shrink-0 p-0.5 opacity-60 hover:opacity-100 transition-opacity"
            aria-label="Dismiss notification"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}
